import React from 'react'
import PropTypes from 'prop-types'
import shuffle from 'lodash/shuffle'
import styled from '@emotion/styled'
import { Avatar, color, device, Image, radius, space } from '@ticketswap/solar'

const RowImage = styled.div`
  position: relative;
  opacity: 0.6;
  border-radius: ${radius.md};
  margin: ${space[4]};
  overflow: hidden;

  @media ${device.mobileL} {
    margin: ${space[8]};
  }
`

const ThumbnailRows = styled.div`
  position: absolute;
  display: grid;
  background-color: ${color.space};
  grid-template-rows: repeat(3, ${100 / 3}%);
  grid-template-columns: repeat(3, ${100 / 3}%);
  top: -${space[32]};
  left: -${space[96]};
  right: -${space[96]};
  height: calc(${192 / 16}rem + ${space[64]});

  div:nth-of-type(n + 4):nth-of-type(-n + 6) {
    left: 50%;
  }

  @media ${device.mobileL} {
    top: -${space[64]};
    left: -${space[128]};
    right: -${space[128]};
    height: calc(${192 / 16}rem + ${space[128]});
    grid-template-columns: repeat(4, 25%);

    div:nth-of-type(n + 4):nth-of-type(-n + 6) {
      left: 0;
    }

    div:nth-of-type(n + 5):nth-of-type(-n + 8) {
      left: 50%;
    }
  }

  @media ${device.tablet} {
    height: calc(${space[256]} + ${space[128]});
    grid-template-columns: repeat(5, 20%);

    div:nth-of-type(n + 5):nth-of-type(-n + 8) {
      left: 0;
    }

    div:nth-of-type(n + 6):nth-of-type(-n + 10) {
      left: 50%;
    }
  }
`

const CenteredAvatar = styled(Avatar)`
  position: absolute;
  top: ${space[48]};
  left: 50%;
  transform: translateX(-${space[32]});

  @media ${device.tablet} {
    top: ${space[64]};
    transform: translateX(-${space[48]});
    width: ${space[96]};
    height: ${space[96]};
  }
`

const AvatarBackground = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  height: ${192 / 16}rem;
  background-size: cover;
  background-position: center;
  background-image: url(${props => props.imageUrl});
  filter: blur(15px);
  transform: scale(1.2);
  opacity: 0.6;

  @media ${device.tablet} {
    height: ${space[256]};
  }
`

const RowAvatar = styled(Avatar)`
  opacity: 0.6;
  align-self: center;
  justify-self: center;

  @media ${device.mobileL} {
    width: ${72 / 16}rem;
    height: ${72 / 16}rem;
  }

  @media ${device.tablet} {
    width: ${space[96]};
    height: ${space[96]};
  }
`

const AvatarRows = styled.div`
  position: absolute;
  display: grid;
  background-color: ${color.space};
  grid-template-rows: repeat(3, ${100 / 3}%);
  grid-template-columns: repeat(5, 20%);
  top: -${space[16]};
  left: -${space[32]};
  right: -${space[32]};
  height: calc(${192 / 16}rem + ${space[32]});
  overflow: hidden;

  div:nth-of-type(n + 6):nth-of-type(-n + 10) {
    margin-left: 100%;
  }

  div:nth-of-type(n + 16):nth-of-type(-n + 20) {
    margin-left: 100%;
  }

  @media ${device.tablet} {
    grid-template-columns: repeat(8, ${100 / 8}%);
    top: -${space[32]};
    left: -${space[96]};
    right: -${space[96]};
    height: calc(${space[256]} + ${space[64]});

    div:nth-of-type(n + 6):nth-of-type(-n + 10) {
      margin-left: 0;
    }

    div:nth-of-type(n + 16):nth-of-type(-n + 20) {
      margin-left: 0;
    }

    div:nth-of-type(n + 8):nth-of-type(-n + 17) {
      margin-left: 100%;
    }
  }
`

const ListImageRow = ({ thumbnailImages, hasAvatars }) => {
  const thumbnailGroups = React.useMemo(() => {
    if (thumbnailImages.length === 0) return null

    // When only one avatar we only show that one
    if (hasAvatars && thumbnailImages.length === 1) return null

    const arrayLength = hasAvatars ? 24 : 15
    const lengthenArray = images => {
      if (images.length < arrayLength) {
        images.push(...images)

        return lengthenArray(images)
      }

      if (images.length > arrayLength) {
        images = images.slice(0, arrayLength)
      }

      return images
    }

    const images = [...thumbnailImages]

    return shuffle(lengthenArray(images))
  }, [thumbnailImages, hasAvatars])

  if (thumbnailImages.length === 0) return null

  if (hasAvatars) {
    // When only one avatar we only show that one with a background
    if (thumbnailImages.length === 1) {
      return (
        <>
          <AvatarBackground imageUrl={thumbnailImages[0]} />
          <CenteredAvatar src={thumbnailImages[0]} size={64} />
        </>
      )
    }

    return (
      <AvatarRows>
        {thumbnailGroups.map((image, index) => (
          <RowAvatar key={index} src={image} size={64} />
        ))}
      </AvatarRows>
    )
  }

  return (
    <ThumbnailRows>
      {thumbnailGroups.map((image, index) => (
        <RowImage key={index}>
          <Image src={image} />
        </RowImage>
      ))}
    </ThumbnailRows>
  )
}

ListImageRow.defaultProps = {
  hasAvatars: false,
}

ListImageRow.propTypes = {
  thumbnailImages: PropTypes.arrayOf(PropTypes.string).isRequired,
  hasAvatars: PropTypes.bool,
}

export default ListImageRow
